import React from 'react';
import { TaraCard, TaraCardHeader, TaraCardTitle, TaraCardContent } from '../ui/TaraCard';
import TaraBadge from '../ui/TaraBadge';
import { Plane, MapPin, Compass, AlertTriangle, Globe } from 'lucide-react';

const TravelNavigator = () => {
  return (
    <div className="space-y-6">
      {/* Travel Readiness Score */}
      <TaraCard variant="cosmic">
        <TaraCardHeader>
          <TaraCardTitle>Travel Readiness Index</TaraCardTitle>
          <p className="text-sm text-stone-600 mt-1">
            Current planetary alignment for journeys
          </p>
        </TaraCardHeader>
        <TaraCardContent className="p-6">
          <div className="text-center mb-6">
            <div className="text-5xl font-bold text-teal-600 mb-2">72%</div>
            <TaraBadge variant="calm" size="lg">Favorable for Travel</TaraBadge>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-stone-50 rounded-lg">
              <div className="flex items-center gap-2">
                <Plane className="w-4 h-4 text-teal-600" />
                <span className="text-sm font-medium text-stone-900">Long Distance</span>
              </div>
              <span className="text-sm font-bold text-teal-600">Good</span>
            </div>

            <div className="flex items-center justify-between p-3 bg-stone-50 rounded-lg">
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-teal-600" />
                <span className="text-sm font-medium text-stone-900">Short Trips</span>
              </div>
              <span className="text-sm font-bold text-teal-600">Excellent</span>
            </div>

            <div className="flex items-center justify-between p-3 bg-stone-50 rounded-lg">
              <div className="flex items-center gap-2">
                <Globe className="w-4 h-4 text-teal-600" />
                <span className="text-sm font-medium text-stone-900">Foreign Travel</span>
              </div>
              <span className="text-sm font-bold text-amber-600">Moderate</span>
            </div>
          </div>
        </TaraCardContent>
      </TaraCard>

      {/* Favorable Travel Windows */}
      <TaraCard>
        <TaraCardHeader>
          <TaraCardTitle className="flex items-center gap-2">
            <Compass className="w-5 h-5 text-teal-600" />
            Optimal Travel Windows
          </TaraCardTitle>
        </TaraCardHeader>
        <TaraCardContent className="p-6">
          <div className="space-y-4">
            <div className="p-4 bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg border-l-4 border-green-500">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <p className="text-sm font-bold text-stone-900">April - June 2025</p>
                  <p className="text-xs text-stone-600 mt-0.5">Best travel period</p>
                </div>
                <TaraBadge variant="vibrant" size="sm">Highly Favorable</TaraBadge>
              </div>
              <p className="text-xs text-stone-700 leading-relaxed">
                Jupiter transits your 9th house. Perfect for international travel, pilgrimage, and exploration. Success in all journeys.
              </p>
            </div>

            <div className="p-4 bg-gradient-to-br from-blue-50 to-cyan-50 rounded-lg border-l-4 border-blue-500">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <p className="text-sm font-bold text-stone-900">September 2025</p>
                  <p className="text-xs text-stone-600 mt-0.5">Secondary window</p>
                </div>
                <TaraBadge variant="calm" size="sm">Good</TaraBadge>
              </div>
              <p className="text-xs text-stone-700 leading-relaxed">
                Venus dasha favorable for pleasure trips and holidays. Good for romantic getaways.
              </p>
            </div>
          </div>
        </TaraCardContent>
      </TaraCard>

      {/* Caution Periods */}
      <TaraCard>
        <TaraCardHeader>
          <TaraCardTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            Periods to Avoid
          </TaraCardTitle>
        </TaraCardHeader>
        <TaraCardContent className="p-6">
          <div className="space-y-3">
            <div className="p-4 bg-amber-50 rounded-lg border-l-4 border-amber-500">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <p className="text-sm font-bold text-stone-900">November 15-18, 2025</p>
                  <p className="text-xs text-stone-600 mt-0.5">High delay risk</p>
                </div>
                <TaraBadge variant="watchful" size="sm">Caution</TaraBadge>
              </div>
              <p className="text-xs text-stone-700">
                Mars transit creates travel disruptions. Expect delays, cancellations, or obstacles. Postpone if possible.
              </p>
            </div>

            <div className="p-4 bg-stone-50 rounded-lg border-l-4 border-stone-300">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <p className="text-sm font-bold text-stone-900">December 2025</p>
                  <p className="text-xs text-stone-600 mt-0.5">Saturn pressure</p>
                </div>
                <TaraBadge variant="neutral" size="sm">Not Ideal</TaraBadge>
              </div>
              <p className="text-xs text-stone-700">
                Saturn aspects 9th house. Travel will feel burdensome. Required trips only.
              </p>
            </div>
          </div>
        </TaraCardContent>
      </TaraCard>

      {/* Directional Guidance */}
      <TaraCard>
        <TaraCardHeader>
          <TaraCardTitle className="flex items-center gap-2">
            <MapPin className="w-5 h-5 text-teal-600" />
            Favorable Directions
          </TaraCardTitle>
          <p className="text-sm text-stone-600 mt-1">
            Best geographical zones based on your chart
          </p>
        </TaraCardHeader>
        <TaraCardContent className="p-6">
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="p-3 bg-green-50 rounded-lg text-center">
              <p className="text-2xl mb-1">🧭</p>
              <p className="text-sm font-bold text-stone-900">East</p>
              <TaraBadge variant="vibrant" size="sm" className="mt-1">Highly Favorable</TaraBadge>
            </div>

            <div className="p-3 bg-blue-50 rounded-lg text-center">
              <p className="text-2xl mb-1">⬆️</p>
              <p className="text-sm font-bold text-stone-900">North-East</p>
              <TaraBadge variant="calm" size="sm" className="mt-1">Good</TaraBadge>
            </div>
          </div>

          <div className="text-xs text-stone-700 space-y-2">
            <p>
              <strong>2025-2027:</strong> East and North-East directions blessed by Jupiter. Consider destinations in these zones.
            </p>
            <p>
              <strong>Best countries:</strong> Japan, Thailand, Vietnam, New Zealand (East); Nepal, Bhutan (North-East)
            </p>
          </div>
        </TaraCardContent>
      </TaraCard>

      {/* Migration Potential */}
      <TaraCard variant="sacred">
        <TaraCardHeader>
          <TaraCardTitle>Permanent Relocation Analysis</TaraCardTitle>
        </TaraCardHeader>
        <TaraCardContent className="p-6">
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-stone-900">Migration Probability (Next 5 Years)</p>
              <TaraBadge variant="vibrant" size="sm">High (78%)</TaraBadge>
            </div>
            <div className="w-full bg-stone-200 rounded-full h-2">
              <div className="bg-teal-600 h-2 rounded-full" style={{ width: '78%' }} />
            </div>
          </div>

          <div className="space-y-2.5 text-sm">
            <div className="flex items-start gap-2">
              <span className="text-teal-600 font-bold">•</span>
              <span className="text-stone-700">
                <strong>Peak window: 2027-2028</strong> - Rahu-Jupiter alignment strongly favors foreign settlement
              </span>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-teal-600 font-bold">•</span>
              <span className="text-stone-700">
                Start visa/immigration processes in early 2026 for best results
              </span>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-teal-600 font-bold">•</span>
              <span className="text-stone-700">
                Your 12th house activation indicates living far from birthplace brings prosperity
              </span>
            </div>
          </div>
        </TaraCardContent>
      </TaraCard>
    </div>
  );
};

export default TravelNavigator;
